package io

import AppBuildConfig
import AppBuildConfig.HOST_BACKEND
import AppBuildConfig.HOST_FILES
import AppBuildConfig.HOST_REPORTS
import AppBuildConfig.URL_ICONS
import androidx.compose.runtime.MutableState
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import api.CampaignState
import api.LanguageType
import api.VisualProperties
import common.frontend.api.Io
import common.frontend.api.IoReport
import device.DevicePlaylist
import io.ktor.client.call.*
import io.ktor.client.plugins.websocket.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.util.*
import io.ktor.utils.io.charsets.*
import io.ktor.utils.io.core.*
import io.ktor.websocket.*
import kotlinx.coroutines.channels.consumeEach
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.datetime.UtcOffset
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.decodeFromByteArray
import org.jetbrains.skia.Image
import org.jetbrains.skiko.currentNanoTime
import org.ssoft.common.server.icons.IconIO
import org.ssoft.common.tools.Authorization
import org.ssoft.common.tools.SYSTEM
import org.ssoft.server.boundary.icons.PROTO_INSTANCE_ICONS
import srializable.JSON_INSTANCE
import srializable.PROTO_INSTANCE
import ssoft.frontend.generated.resources.*
import tk.mallumo.common.msg.Message
import tk.mallumo.log.logINFO
import tk.mallumo.utils.DataState
import tools.await
import kotlin.time.Duration.Companion.minutes

class RepoApi(language: StateFlow<LanguageType>) : ImplRepo(language) {

    fun authHash(): String = "Basic ${"$name:$pass".toByteArray(Charsets.UTF_8).encodeBase64()}"

    private suspend fun DefaultClientWebSocketSession.screenshotRequest(
        source: String,
        target: String,
        captureMode: Int
    ) {
        runCatching {
            send(
                Message.encode(
                    if (captureMode == 0) {
                        Message.Screenshot(
                            id = currentNanoTime(),
                            source = source,
                            target = target,
                            statusInt = 0,
                            statusStr = ""
                        )
                    } else {
                        Message.Displayshot(
                            id = currentNanoTime(),
                            source = source,
                            target = target,
                            statusInt = 0,
                            statusStr = ""
                        )
                    }
                )
            )
        }.onFailure {
            logINFO("send fail")
            it.printStackTrace()
        }
    }

    private var lastDeviceSession: Pair<String, DefaultClientWebSocketSession>? = null

    suspend fun sendTouchEvent(mac: String, event: Message.TouchscreenEvent) {
        kotlin.runCatching {
            lastDeviceSession?.apply {
                second.send(
                    Message.encode(
                        msg = Message.Task(
                            id = currentNanoTime(),
                            source = first,
                            target = mac,
                            statusInt = 0,
                            statusStr = "",
                            key = Message.Task.Keys.REMOTE_TOUCHSCREEN.toString()
                        ),
                        extras = JSON_INSTANCE.encodeToString(
                            event
                        ).toByteArray()
                    )
                )
            }
        }.onFailure {
            println("touch fail")
            it.printStackTrace()
        }
    }

    suspend fun remoteScroll(mac: String, x: Int, y: Int, amount: Int) {
        sendTouchEvent(
            mac = mac,
            event = Message.TouchscreenEvent(
                mode = Message.TouchscreenEvent.MODE_SCROLL,
                amount = amount,
                x = x,
                y = y
            )
        )
    }

    suspend fun remoteTouch(mac: String, x: Int, y: Int) {
        sendTouchEvent(
            mac = mac,
            event = Message.TouchscreenEvent(
                mode = Message.TouchscreenEvent.MODE_CLICK,
                amount = 10,
                x = x,
                y = y
            )
        )
    }

    suspend fun screenshot(state: MutableState<DataState<ImageBitmap>>, userId: Long, mac: String, captureMode: Int) =
        coroutineScope {
            val id = "$userId-${currentNanoTime()}"
            state.value = DataState.Loading()

            runCatching {
                val (_, pass) = Authorization.createAuth(id)
                Repository.api.client.ws().use {
                    it.webSocket(
                        method = HttpMethod.Get,
                        host = AppBuildConfig.HOST_BALANCER,
                        port = AppBuildConfig.PORT_BALANCER,
                        path = "/web/$pass",
                        request = {
                            url.protocol = URLProtocol.WSS
                            url.port = port
                        }
                    ) {
                        lastDeviceSession = id to this
                        launch {
                            delay(10)
                            screenshotRequest(id, mac, captureMode)
                        }
                        incoming.consumeEach { message ->
                            if (message is Frame.Binary) {
                                if (handleMessage(state, message.readBytes())) {
                                    if (isActive) screenshotRequest(id, mac, captureMode)
                                }
                            }
                        }
                    }
                }
            }.onFailure {
                lastDeviceSession = null
                logINFO("WS connection fail")
                state.value = DataState.Error("network connection fail")
            }

            lastDeviceSession = null
        }

    private suspend fun handleMessage(state: MutableState<DataState<ImageBitmap>>, readBytes: ByteArray): Boolean {
        val (msg, data) = Message.decode(readBytes)
        return when (msg) {
            is Message.Displayshot,
            is Message.Screenshot -> {
                if (msg.statusInt == Message.STATUS_OK) {
                    state.value = DataState.Result(
                        Image.makeFromEncoded(data!!).toComposeImageBitmap()
                    )
                } else {
                    state.value = DataState.Error(msg.statusStr)
                }
                true
            }

            is Message.Status -> {
                when (msg.statusInt) {
                    Message.STATUS_OK -> false
                    else -> {
                        state.value = DataState.Error(msg.statusStr)
                        true
                    }
                }
            }

            else -> false
        }
    }

    suspend fun fetchTranslation(lang: LanguageType): String {
        return client.raw().use { httpClient ->
            httpClient.get("/composeResources/ssoft.frontend.generated.resources/${lang.directoryName}/strings.commonMain.cvr")
                .throwOnFail()
                .bodyAsText()
        }
    }

    private lateinit var name: String
    private lateinit var pass: String

    val server = object : ImplApiServer {

        override suspend fun listing(): Io.ListingServer {
            return client.json(name, pass).use { httpClient ->
                httpClient.get("$HOST_BACKEND/api/server/listing")
                    .throwOnFail()
                    .body<Io.ListingServer>()
            }
        }

        override suspend fun single(serverId: Long): Io.Server {
            return client.json(name, pass).use { httpClient ->
                httpClient.get("$HOST_BACKEND/api/server/single/$serverId")
                    .throwOnFail()
                    .body<Io.Server>()
            }
        }

        override suspend fun add(server: Io.ListingServer.Item): Io.ListingServer.Item {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/server") {
                    setBody(server)
                    contentType(ContentType.Application.Json)
                }.throwOnFail().body<Io.ListingServer.Item>()
            }
        }

        override suspend fun edit(server: Io.Server) {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/server") {
                    setBody(server)
                    contentType(ContentType.Application.Json)
                }.throwOnFail()
            }
        }

        override suspend fun delete(serverId: Long) {
            return client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/server/${serverId}")
                    .throwOnFail()
            }
        }

        override suspend fun swap(serverId: Long) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/server/${serverId}/swap")
                    .throwOnFail()
            }
        }

        override suspend fun info(): Io.ServerInfo {
            return client.json().use {
                it.get("$HOST_BACKEND/api/public/info")
                    .throwOnFail()
                    .body()
            }
        }

        override suspend fun lock(serverId: Long) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/server/${serverId}/lock")
                    .throwOnFail()
            }
        }

    }
    val user = object : ImplApiUser {
        override suspend fun login(name: String, pass: String): Io.User {
            return client.json(name, pass).use { httpClient ->
                httpClient.get("$HOST_BACKEND/api/public/login")
                    .takeIf { it.status.isSuccess() }
                    ?.also {
                        this@RepoApi.name = name
                        this@RepoApi.pass = pass
                    }
                    ?.body<Io.User>()
                    ?: error("Unauthorized")
            }
        }

        override suspend fun single(id: Long): Io.User {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/user/single/$id")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            else -> null
                        }
                    }
                    .body<Io.User>()
            }
        }

        override suspend fun listingAll(): Io.ListingUser {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/user/listing/all")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
                    .body<Io.ListingUser>()
            }
        }

        override suspend fun listing(companyId: Long): Io.ListingUser {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/user/listing/$companyId")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
                    .body<Io.ListingUser>()
            }
        }

        override suspend fun edit(user: Io.User) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/user") {
                    contentType(ContentType.Application.Json)
                    setBody(user)
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        else -> null
                    }
                }
            }
        }

        override suspend fun editPermission(userId: Long, permission: Io.Permission) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/user/$userId/permission") {
                    contentType(ContentType.Application.Json)
                    setBody(permission)
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        else -> null
                    }
                }
            }
        }

        override suspend fun add(companyId: Long, user: Io.ListingUser.Item): Io.ListingUser.Item {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/user/${companyId}") {
                    setBody(user)
                    contentType(ContentType.Application.Json)
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        else -> null
                    }
                }.body<Io.ListingUser.Item>()
            }
        }

        override suspend fun delete(id: Long) {
            return client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/user/${id}")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
            }
        }

        override suspend fun passwordChange(id: Long, name: String, newPass: String) {
            client.json(name, "123456").use {
                it.post("$HOST_BACKEND/api/user/${id}/password") {
                    setBody(newPass)
                    contentType(ContentType.Text.Plain)
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        else -> null
                    }
                }
            }
            this@RepoApi.name = name
            this@RepoApi.pass = newPass
        }

        override suspend fun passwordReset(id: Long) {
            client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/user/${id}/password")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
            }
        }
    }

    val company = object : ImplApiCompany {

        override suspend fun single(id: Long): Io.Company {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/company/single/$id")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
                    .body<Io.Company>()
            }
        }

        override suspend fun listing(): Io.ListingCompany {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/company/listing")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
                    .body<Io.ListingCompany>()
            }
        }

        override suspend fun edit(company: Io.Company) {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/company") {
                    contentType(ContentType.Application.Json)
                    setBody(company)
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        else -> null
                    }
                }
            }
        }

        override suspend fun add(company: Io.ListingCompany.Item): Io.ListingCompany.Item {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/company") {
                    setBody(company)
                    contentType(ContentType.Application.Json)
                }.throwOnFail().body<Io.ListingCompany.Item>()
            }
        }

        override suspend fun delete(id: Long) {
            return client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/company/${id}")
                    .throwOnFail()
            }
        }
    }

    val device = object : ImplApiDevice {

        override suspend fun info(id: Long, lang: LanguageType): Io.DeviceInfo {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/device/info/$id/${lang.type}")
                    .throwOnFail()
                    .body<Io.DeviceInfo>()
            }
        }

        override suspend fun editorGet(id: Long, lang: LanguageType): Io.DeviceEditor {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/device/edit/$id/${lang.type}")
                    .throwOnFail()
                    .body<Io.DeviceEditor>()
            }
        }

        override suspend fun registration(key: String, lang: LanguageType): Io.DeviceEditor {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/device/edit/$key/${lang.type}")
                    .throwOnFail()
                    .body<Io.DeviceEditor>()
            }
        }

        override suspend fun editorSet(data: Io.DeviceEditor) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/device/edit") {
                    setBody(data)
                    contentType(ContentType.Application.Json)
                }.throwOnFail()
            }
        }

        override suspend fun action(id: Long, task: String) {
            val result = client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/device/$id/action/$task").throwOnFail()
            }
            when (result.status) {
                HttpStatusCode.OK -> Unit
                HttpStatusCode.BadRequest -> error("Undefined device action")
                HttpStatusCode.MethodNotAllowed -> error("Conflict with device settings")
                HttpStatusCode.Unauthorized -> error("You have no permission to change Operation mode of this device")
                HttpStatusCode.NotFound -> error("Device is not connected (usually no internet)")
                else -> error("Unexpected failure ${result.status}")
            }
        }

        override suspend fun listing(companyId: Long, locationId: Long): Io.ListingDevice {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/device/listing/$companyId/$locationId")
                    .onFailHandled()
                    .body<Io.ListingDevice>()
            }
        }

        override suspend fun listingAllByServer(): Io.ListingDevice {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/device/listing/all")
                    .onFailHandled()
                    .body<Io.ListingDevice>()
            }
        }

        override suspend fun delete(id: Long) {
            return client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/device/${id}")
                    .throwOnFail()
            }
        }

        override suspend fun bundleCompanyWithLocation(): Io.BundleCompanyWithLocation {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/device/bundle/company/location")
                    .throwOnFail()
                    .body<Io.BundleCompanyWithLocation>()
            }
        }
    }
    val resource = object : ImplApiResource {

        override suspend fun listing(companyId: Long, restriction: Int): Io.ListingResource {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/resource/listing/$companyId/$restriction")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
                    .body<Io.ListingResource>()
            }
        }

        override suspend fun delete(companyId: Long, resourceId: Long) {
            client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/resource/single/$companyId/$resourceId")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
            }
        }

        override suspend fun single(companyId: Long, resourceId: Long): Io.Resource {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/resource/single/$companyId/$resourceId")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            else -> null
                        }
                    }
                    .body<Io.Resource>()
            }
        }

        override suspend fun transform(companyId: Long, resourceId: Long, props: VisualProperties): Io.ListingResource.Item {
            return client.json(name, pass, 30.minutes).use {
                it.post("$HOST_BACKEND/api/resource/single/$companyId/${resourceId}") {
                    contentType(ContentType.Application.Json)
                    setBody(JSON_INSTANCE.encodeToString(props))
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        else -> null
                    }
                }.body<Io.ListingResource.Item>()
            }
        }

        override fun buildFileUploadUrl(): String {
            return "$HOST_FILES/api/upload"
        }

        override fun buildLicenseUploadUrl(companyId: Long): String {
            return "$HOST_BACKEND/api/license/single/$companyId"
        }
    }

    val location = object : ImplApiLocation {

        override suspend fun emergencyActivate(locationId: Long, resourceId: Long) {
            client.raw(name, pass, 5.minutes).use {
                it.post("$HOST_BACKEND/api/location/${locationId}/emergency/$resourceId")
                    .throwOnFail()
            }
        }

        override suspend fun emergencyDisable(locationId: Long) {
            client.raw(name, pass, 5.minutes).use {
                it.delete("$HOST_BACKEND/api/location/${locationId}/emergency")
                    .throwOnFail()
            }
        }

        override suspend fun single(id: Long): Io.Location {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/location/single/$id")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            else -> null
                        }
                    }
                    .body<Io.Location>()
            }
        }

        override suspend fun listing(companyId: Long): Io.ListingLocation {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/location/listing/$companyId")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            else -> null
                        }
                    }.body<Io.ListingLocation>()
            }
        }

        override suspend fun listingAll(): Io.ListingLocation {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/location/listing/all")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            else -> null
                        }
                    }.body<Io.ListingLocation>()
            }
        }

        override suspend fun edit(location: Io.Location) {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/location") {
                    contentType(ContentType.Application.Json)
                    setBody(location)
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                        else -> null
                    }
                }
            }
        }

        override suspend fun add(companyId: Long, location: Io.ListingLocation.Item): Io.ListingLocation.Item {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/location/${companyId}") {
                    setBody(location)
                    contentType(ContentType.Application.Json)
                }.onFailHandled { statusCode ->
                    when (statusCode) {
                        HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                        HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                        HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                        else -> null
                    }
                }.body<Io.ListingLocation.Item>()
            }
        }

        override suspend fun delete(id: Long) {
            return client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/location/${id}")
                    .onFailHandled { statusCode ->
                        when (statusCode) {
                            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
                            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
                            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
                            else -> null
                        }
                    }
            }
        }

        override suspend fun groupAction(locationId: Long, act: Message.Task.Keys) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/location/$locationId/action/${act.name}").throwOnFail()
            }
        }
    }

    val playlist = object : ImplApiPlaylist {

        override suspend fun single(deviceId: Long): DevicePlaylist {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/playlist/${deviceId}")
                    .throwOnFail()
                    .body<DevicePlaylist>()

            }
        }

        override suspend fun downloadExcel(deviceId: Long): ByteArray {
            return client.raw(name, pass, 5.minutes).use {
                it.get("$HOST_BACKEND/api/playlist/${deviceId}/zip")
                    .throwOnFail()
                    .body()

            }
        }

        override suspend fun reorder(deviceId: Long, newOrder: Io.ListingPlaylistOrder) {
            client.json(name, pass).use {
                val resp = it.post("$HOST_BACKEND/api/playlist/${deviceId}/reorder") {
                    contentType(ContentType.Application.Json)
                    setBody(JSON_INSTANCE.encodeToString(newOrder))
                }

                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to edit playlist")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.BadRequest -> error("Missing parameters")
                    HttpStatusCode.OK -> Unit
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }
    }

    val rss = object : ImplApiRss {
        override suspend fun reload(id: Long) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/rss/${id}/reload")
                    .onFailHandled()
            }
        }

        override suspend fun state(id: Long): Io.RssState {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/rss/${id}/state")
                    .onFailHandled()
                    .body<Io.RssState>()
            }
        }

        override suspend fun listingChannel(): Io.ListingRssChannel {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/rss/listing/channel")
                    .onFailHandled()
                    .body<Io.ListingRssChannel>()
            }
        }

        override suspend fun listingInstance(): Io.ListingRssInstance {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/rss/listing/instance")
                    .onFailHandled()
                    .body<Io.ListingRssInstance>()
            }
        }

        override suspend fun preview(url: String): api.org.ssoft.server.common.rss.Io.Rss {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/rss/preview") {
                    contentType(ContentType.Text.Plain)
                    setBody(url)
                }
                    .onFailHandled()
                    .body<api.org.ssoft.server.common.rss.Io.Rss>()
            }
        }

        override suspend fun channelEdit(item: Io.ListingRssChannel.Item) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/rss/channel") {
                    contentType(ContentType.Application.Json)
                    setBody(item)
                }.onFailHandled()
            }
        }

        override suspend fun channelCreate(item: Io.ListingRssChannel.Item): Io.ListingRssChannel.Item {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/app/rss/channel") {
                    contentType(ContentType.Application.Json)
                    setBody(item)
                }.onFailHandled()
                    .body<Io.ListingRssChannel.Item>()
            }
        }

        override suspend fun instanceCreate(item: Io.RssInstance.Item): Io.RssInstance.Item {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/app/rss/instance") {
                    contentType(ContentType.Application.Json)
                    setBody(item)
                }.onFailHandled()
                    .body<Io.RssInstance.Item>()
            }
        }

        override suspend fun instanceEdit(item: Io.RssInstance.Item) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/rss/instance/${item.id}") {
                    contentType(ContentType.Application.Json)
                    setBody(item)
                }.onFailHandled()
            }
        }

        override suspend fun instanceDelete(rssInstanceId: Long) {
            client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/app/rss/instance/$rssInstanceId")
                    .onFailHandled()
            }
        }


        override suspend fun instanceDetail(id: Long): Io.RssInstance {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/rss/instance/$id")
                    .onFailHandled()
                    .body<Io.RssInstance>()
            }
        }

        override suspend fun instanceTemplate(lang: String): Io.RssInstance {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/rss/instance/template/$lang")
                    .onFailHandled()
                    .body<Io.RssInstance>()
            }
        }
    }

    val game = object : ImplApiGame {
        override suspend fun wheelHistory(
            companyId: Long,
            appId: Long
        ): Io.WheelHistoryOverview {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/game/$companyId/wheel/$appId/history")
                    .onFailHandled()
                    .body<Io.WheelHistoryOverview>()
            }
        }
    }

    val app = object : ImplApiApp {
        override suspend fun perDeviceListing(companyId: Long, deviceId: Long): Io.ListingApp {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/listing/${companyId}/app/${deviceId}")
                    .onFailHandled()
                    .body<Io.ListingApp>()

            }
        }

        override suspend fun perDeviceReorder(companyId: Long, deviceId: Long, appIds: List<Long>) {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/listing/${companyId}/app/${deviceId}/reorder") {
                    contentType(ContentType.Application.Json)
                    setBody(JSON_INSTANCE.encodeToString(appIds))
                }.onFailHandled()

            }
        }

        override suspend fun listingVariant(companyId: Long): Io.ListingAppVariant {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/listing/${companyId}/variant")
                    .onFailHandled()
                    .body<Io.ListingAppVariant>()

            }
        }

        override suspend fun listingApps(companyId: Long): Io.ListingApp {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/listing/${companyId}/app")
                    .onFailHandled()
                    .body<Io.ListingApp>()

            }
        }

        override suspend fun single(companyId: Long, appId: Long): Io.AppDetail {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/single/${companyId}/${appId}")
                    .onFailHandled()
                    .body<Io.AppDetail>()

            }
        }

        override suspend fun archive(companyId: Long, appId: Long, doArchive: Boolean) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/archive/${companyId}/${appId}/${doArchive}")
                    .onFailHandled()

            }
        }

        override suspend fun add(companyId: Long, variantId: Long, lang: String): Long {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/app/single/${companyId}/${variantId}/$lang")
                    .onFailHandled()
                    .bodyAsText()
                    .toLong()

            }
        }

        override suspend fun edit(companyId: Long, app: Io.AppDetail): Io.AppDetail {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/single/${companyId}/${app.id}") {
                    contentType(ContentType.Application.Json)
                    setBody(JSON_INSTANCE.encodeToString(app))
                }.onFailHandled().body<Io.AppDetail>()

            }
        }

        override suspend fun delete(companyId: Long, appId: Long, withLogo: Boolean, withContent: Boolean) {
            client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/app/single") {
                    contentType(ContentType.Application.Json)
                    setBody(
                        JSON_INSTANCE.encodeToString(
                            Io.AppDelete(
                                companyId = companyId,
                                appId = appId,
                                keepLogo = !withLogo,
                                keepContent = !withContent
                            )
                        )
                    )
                }.onFailHandled()
            }
        }


        override suspend fun visibilityListing(): Io.ListingAppManager {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/app/visibility/listing")
                    .onFailHandled()
                    .body<Io.ListingAppManager>()

            }
        }

        override suspend fun visibilityChange(data: Io.ListingAppManager) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/app/visibility/listing") {
                    contentType(ContentType.Application.Json)
                    setBody(JSON_INSTANCE.encodeToString(data))
                }.onFailHandled()
            }
        }

    }

    @OptIn(ExperimentalSerializationApi::class)
    val icons = object : ImplIcons {

        override suspend fun random(): IconIO.Result {
            return client.raw().use {
                it.get("${URL_ICONS}/api/listing/random")
                    .onFailHandled()
                    .bodyAsBytes()
                    .let { PROTO_INSTANCE_ICONS.decodeFromByteArray<IconIO.Result>(it) }

            }
        }

    }
    val report = object : ImplReport {
        override suspend fun listingDeviceSystem(deviceId: Long, offset: Int): IoReport.ListingDeviceSystem {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/device/$deviceId/report/system/$offset")
                    .onFailHandled()
                    .body<IoReport.ListingDeviceSystem>()

            }
        }

        override suspend fun listingDeviceAlive(deviceId: Long, offset: Int): IoReport.ListingDeviceAlive {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/device/$deviceId/report/alive/$offset")
                    .onFailHandled()
                    .body<IoReport.ListingDeviceAlive>()
            }
        }

        //  http://localhost:7777/api/report/{companyId}/campaign/{campaignGroupId}
        override suspend fun campaignGroup(companyId: Long, campaignGroupId: Long): Io.ReportCampaign {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/report/$companyId/campaign/$campaignGroupId")
                    .onFailHandled()
                    .body<Io.ReportCampaign>()
            }
        }

        override suspend fun campaignGroupImpression(
            companyId: Long,
            campaignGroupId: Long
        ): IoReport.ReportImpression {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/report/$companyId/campaign/$campaignGroupId/impression")
                    .onFailHandled()
                    .body<IoReport.ReportImpression>()
            }
        }

        override suspend fun appQuiz(companyId: Long, appId: Long, offsetReportId: Long): IoReport.ReportAppQuiz {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/report/$companyId/app/$appId/history/$offsetReportId/quiz")
                    .onFailHandled()
                    .body<IoReport.ReportAppQuiz>()
            }
        }
    }

    val translation = object : ImplApiTranslation {

        override suspend fun listing(): Io.ListingTranslation {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/translation/listing")
                    .throwOnFail()
                    .body<Io.ListingTranslation>()

            }
        }

        override suspend fun edit(data: Io.ListingTranslation): Io.ListingTranslation {
            return client.json(name, pass).use {
                val resp = it.patch("$HOST_BACKEND/api/translation/listing") {
                    contentType(ContentType.Application.Json)
                    setBody(data)
                }
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to edit translations")
                    HttpStatusCode.OK -> resp.body<Io.ListingTranslation>()
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun add(json: String): String {
            return client.json(name, pass).use {
                val resp = it.put("$HOST_BACKEND/api/translation/listing") {
                    contentType(ContentType.Application.Json)
                    setBody(json)
                }
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to add new translations")
                    HttpStatusCode.NotFound -> error("Create fail, try later")
                    HttpStatusCode.OK -> resp.status.description
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

    }
    val campaign = object : ImplApiCampaign {
        override suspend fun listing(companyId: Long, filter: Io.Filter.CampaignListing): Io.ListingCampaign {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/campaign/listing/company/${companyId}") {
                    contentType(ContentType.Application.Json)
                    setBody(filter)
                }
                    .throwOnFail()
                    .body<Io.ListingCampaign>()

            }
        }

        override suspend fun single(companyId: Long, campaignId: Long): Io.Campaign {
            return client.json(name, pass).use {
                val resp = it.get("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to add new campaign")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.OK -> resp.body<Io.Campaign>()
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun singleByGroupId(companyId: Long, campaignGroupId: Long): Io.Campaign {
            return client.json(name, pass).use {
                val resp = it.get("$HOST_BACKEND/api/campaign/company/${companyId}/group/${campaignGroupId}")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to add new campaign")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.OK -> resp.body<Io.Campaign>()
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun template(companyId: Long): Io.Campaign {
            return client.json(name, pass).use {
                val resp = it.get("$HOST_BACKEND/api/campaign/company/${companyId}")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to add new campaign")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.OK -> resp.body<Io.Campaign>()
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun create(companyId: Long, campaign: Io.Campaign): Io.Campaign {
            return client.json(name, pass).use {
                val resp = it.post("$HOST_BACKEND/api/campaign/company/${companyId}") {
                    contentType(ContentType.Application.Json)
                    setBody(JSON_INSTANCE.encodeToString(campaign))
                }
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to edit campaign")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.NotAcceptable -> error("Campaign can not be edited in current state")
                    HttpStatusCode.OK -> resp.body<Io.Campaign>()
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun changes(companyId: Long, campaign: Io.Campaign): Io.Campaign {
            return client.json(name, pass).use {
                val resp = it.patch("$HOST_BACKEND/api/campaign/company/${companyId}") {
                    contentType(ContentType.Application.Json)
                    setBody(JSON_INSTANCE.encodeToString(campaign))
                }
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to edit campaign")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.NotAcceptable -> error("Campaign can not be edited in current state")
                    HttpStatusCode.Conflict -> error("Campaign can not be edited in current state")
                    HttpStatusCode.OK -> resp.body<Io.Campaign>()
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun changeState(
            companyId: Long,
            campaignId: Long,
            newState: CampaignState,
            expectedImpressions: Long
        ) {
            return client.json(name, pass).use {
                val timezone = UtcOffset.SYSTEM.toString()

                val resp = when (newState) {
                    CampaignState.STOPPED -> it.patch("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}/$timezone")
                    CampaignState.DELETED -> it.delete("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}")
                    CampaignState.ACTIVE,
                    CampaignState.PAUSED,
                    CampaignState.PUBLISHED -> it.patch("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}/$timezone/${expectedImpressions}")

                    CampaignState.FINISHED,
                    CampaignState.DRAFT -> error("invalid new state -> ${newState.label}")
                }

                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to change state of campaign")
                    HttpStatusCode.NotAcceptable -> error("Campaign can not be changed into this state")
                    HttpStatusCode.OK -> Unit
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun clone(companyId: Long, campaignId: Long): Long {
            return client.json(name, pass).use {
                val resp = it.put("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to create clone of campaign")
                    HttpStatusCode.InternalServerError -> error("Create fail, try later")
                    HttpStatusCode.OK -> resp.bodyAsText().toLongOrNull() ?: error("Campaign clone failure")
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun devicesCheckSlot(companyId: Long, campaignId: Long): Io.ListingCampaignDevicesCheck {
            return client.json(name, pass).use {
                val resp = it.get("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}/check/slotV3")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to check devices usage")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.BadRequest -> error("Missing arguments")
                    HttpStatusCode.OK -> resp.readRawBytes().let {
                        PROTO_INSTANCE.decodeFromByteArray<Io.ListingCampaignDevicesCheck>(it)
                    }

                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun devicesCheckDeviceDoy(companyId: Long, campaignId: Long, doy: Int, deviceId: Long): Io.ListingCampaignDeviceDoyCheck {
            return client.json(name, pass).use {
                val resp = it.get("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}/check/doy/${doy}/${deviceId}")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to check devices usage")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.BadRequest -> error("Missing arguments")
                    HttpStatusCode.OK -> resp.readRawBytes().let {
                        PROTO_INSTANCE.decodeFromByteArray<Io.ListingCampaignDeviceDoyCheck>(it)
                    }

                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun devicesCheckImpression(companyId: Long, campaignId: Long): Io.ListingCampaignDevicesCheck {
            return client.json(name, pass).use {
                val resp = it.get("$HOST_BACKEND/api/campaign/company/${companyId}/${campaignId}/check/impression")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to check devices usage")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.BadRequest -> error("Missing arguments")
                    HttpStatusCode.OK -> resp.readRawBytes().let {
                        PROTO_INSTANCE.decodeFromByteArray<Io.ListingCampaignDevicesCheck>(it)
                    }

                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }
    }

    val hardware = object : ImplHardware {
        override suspend fun listing(): Io.ListingHardware {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/hardware/listing")
                    .onFailHandled()
                    .body<Io.ListingHardware>()
            }
        }

        override suspend fun single(id: Long): Io.Hardware {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/hardware/single/$id")
                    .onFailHandled()
                    .body<Io.Hardware>()
            }
        }

        override suspend fun edit(data: Io.Hardware) {
            client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/hardware/single") {
                    contentType(ContentType.Application.Json)
                    setBody(data)
                }.onFailHandled()
            }
        }

        override suspend fun delete(id: Long) {
            client.json(name, pass).use {
                it.delete("$HOST_BACKEND/api/hardware/single/$id")
                    .onFailHandled()
            }
        }

        override suspend fun add(data: Io.Hardware): Io.Hardware {
            return client.json(name, pass).use {
                it.put("$HOST_BACKEND/api/hardware/single") {
                    contentType(ContentType.Application.Json)
                    setBody(data)
                }.onFailHandled()
                    .body<Io.Hardware>()
            }
        }

    }
    var dashboard = object : ImplApiDashboard {
        override suspend fun byServer(serverId: Long): IoReport.Dashboard = client.json(name, pass).use {
            it.get("$HOST_REPORTS/api/dashboard/0/server/$serverId")
                .onFailHandled()
                .body()
        }

        override suspend fun byCompany(serverId: Long, companyId: Long): IoReport.Dashboard = client.json(name, pass).use {
            it.get("$HOST_REPORTS/api/dashboard/0/server/$serverId/company/$companyId")
                .onFailHandled()
                .body()
        }

    }
    val license = object : ImplLicense {
        override suspend fun listing(): Io.ServerLicensing {
            return client.json(name, pass).use {
                it.get("$HOST_BACKEND/api/license/listing")
                    .onFailHandled()
                    .body<Io.ServerLicensing>()
            }
        }

        override suspend fun history(companyId: Long): Io.CompanyLicenseHistory {
            return client.json(name, pass).use {
                val resp = it.get("$HOST_BACKEND/api/license/single/${companyId}")
                when (resp.status) {
                    HttpStatusCode.Unauthorized -> error("You have no permission to check devices usage")
                    HttpStatusCode.InternalServerError -> error("Fetch fail, try later")
                    HttpStatusCode.BadRequest -> error("Missing arguments")
                    HttpStatusCode.OK -> resp.body<Io.CompanyLicenseHistory>()
                    else -> error("Unexpected failure ${resp.status}")
                }
            }
        }

        override suspend fun approve(id: Long, companyId: Long, message: String): Io.License {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/license/single/${companyId}/$id/approve") {
                    contentType(ContentType.Text.Any)
                    setBody(message)
                }.throwOnFail().body<Io.License>()
            }
        }

        override suspend fun decline(id: Long, companyId: Long, message: String): Io.License {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/license/single/${companyId}/$id/decline") {
                    contentType(ContentType.Text.Any)
                    setBody(message)
                }.throwOnFail().body<Io.License>()
            }
        }

        override suspend fun terminate(id: Long, companyId: Long): Io.License {
            return client.json(name, pass).use {
                it.post("$HOST_BACKEND/api/license/single/${companyId}/$id/terminate")
                    .throwOnFail().body<Io.License>()
            }
        }
    }

    interface ImplApiUser {
        suspend fun login(name: String, pass: String): Io.User
        suspend fun single(id: Long): Io.User
        suspend fun listingAll(): Io.ListingUser
        suspend fun listing(companyId: Long): Io.ListingUser
        suspend fun add(companyId: Long, user: Io.ListingUser.Item): Io.ListingUser.Item
        suspend fun edit(user: Io.User)
        suspend fun editPermission(userId: Long, permission: Io.Permission)
        suspend fun delete(id: Long)
        suspend fun passwordChange(id: Long, name: String, newPass: String)
        suspend fun passwordReset(id: Long)
    }

    interface ImplApiCompany {
        suspend fun single(id: Long): Io.Company
        suspend fun listing(): Io.ListingCompany
        suspend fun add(company: Io.ListingCompany.Item): Io.ListingCompany.Item
        suspend fun edit(company: Io.Company)
        suspend fun delete(id: Long)
    }

    interface ImplApiLocation {
        suspend fun single(id: Long): Io.Location
        suspend fun listing(companyId: Long): Io.ListingLocation
        suspend fun listingAll(): Io.ListingLocation
        suspend fun add(companyId: Long, location: Io.ListingLocation.Item): Io.ListingLocation.Item
        suspend fun edit(location: Io.Location)
        suspend fun delete(id: Long)
        suspend fun groupAction(locationId: Long, act: Message.Task.Keys)
        suspend fun emergencyActivate(locationId: Long, resourceId: Long)
        suspend fun emergencyDisable(locationId: Long)
    }

    interface ImplApiDashboard {
        suspend fun byServer(serverId: Long): IoReport.Dashboard
        suspend fun byCompany(serverId: Long, companyId: Long): IoReport.Dashboard
    }

    interface ImplApiServer {
        suspend fun listing(): Io.ListingServer
        suspend fun single(serverId: Long): Io.Server
        suspend fun add(server: Io.ListingServer.Item): Io.ListingServer.Item
        suspend fun edit(server: Io.Server)
        suspend fun delete(serverId: Long)
        suspend fun lock(serverId: Long)
        suspend fun swap(serverId: Long)
        suspend fun info(): Io.ServerInfo
    }

    interface ImplApiDevice {
        suspend fun info(id: Long, lang: LanguageType): Io.DeviceInfo
        suspend fun delete(id: Long)
        suspend fun action(id: Long, task: String)

        suspend fun registration(key: String, lang: LanguageType): Io.DeviceEditor
        suspend fun editorGet(id: Long, lang: LanguageType): Io.DeviceEditor
        suspend fun editorSet(data: Io.DeviceEditor)

        suspend fun listing(companyId: Long, locationId: Long): Io.ListingDevice
        suspend fun listingAllByServer(): Io.ListingDevice

        suspend fun bundleCompanyWithLocation(): Io.BundleCompanyWithLocation
    }

    interface ImplApiResource {

        suspend fun listing(companyId: Long, restriction: Int): Io.ListingResource

        suspend fun single(companyId: Long, resourceId: Long): Io.Resource

        suspend fun transform(companyId: Long, resourceId: Long, props: VisualProperties): Io.ListingResource.Item

        suspend fun delete(companyId: Long, resourceId: Long)

        fun buildFileUploadUrl(): String

        fun buildLicenseUploadUrl(companyId: Long): String
    }

    interface ImplApiCampaign {
        suspend fun listing(companyId: Long, filter: Io.Filter.CampaignListing): Io.ListingCampaign
        suspend fun single(companyId: Long, campaignId: Long): Io.Campaign
        suspend fun singleByGroupId(companyId: Long, campaignGroupId: Long): Io.Campaign
        suspend fun template(companyId: Long): Io.Campaign
        suspend fun create(companyId: Long, campaign: Io.Campaign): Io.Campaign
        suspend fun changes(companyId: Long, campaign: Io.Campaign): Io.Campaign
        suspend fun changeState(companyId: Long, campaignId: Long, newState: CampaignState, expectedImpressions: Long)
        suspend fun clone(companyId: Long, campaignId: Long): Long
        suspend fun devicesCheckDeviceDoy(companyId: Long, campaignId: Long, doy: Int, deviceId: Long): Io.ListingCampaignDeviceDoyCheck
        suspend fun devicesCheckSlot(companyId: Long, campaignId: Long): Io.ListingCampaignDevicesCheck
        suspend fun devicesCheckImpression(companyId: Long, campaignId: Long): Io.ListingCampaignDevicesCheck
    }

    interface ImplApiGame {
        suspend fun wheelHistory(companyId: Long, appId: Long): Io.WheelHistoryOverview
    }

    interface ImplApiApp {
        suspend fun perDeviceListing(companyId: Long, deviceId: Long): Io.ListingApp
        suspend fun perDeviceReorder(companyId: Long, deviceId: Long, appIds: List<Long>)

        suspend fun listingVariant(companyId: Long): Io.ListingAppVariant
        suspend fun listingApps(companyId: Long): Io.ListingApp
        suspend fun single(companyId: Long, appId: Long): Io.AppDetail

        suspend fun add(companyId: Long, variantId: Long, lang: String): Long
        suspend fun edit(companyId: Long, app: Io.AppDetail): Io.AppDetail
        suspend fun delete(companyId: Long, appId: Long, withLogo: Boolean, withContent: Boolean)
        suspend fun archive(companyId: Long, appId: Long, doArchive: Boolean)

        suspend fun visibilityListing(): Io.ListingAppManager
        suspend fun visibilityChange(data: Io.ListingAppManager)
    }

    interface ImplApiPlaylist {
        suspend fun single(deviceId: Long): DevicePlaylist
        suspend fun downloadExcel(deviceId: Long): ByteArray
        suspend fun reorder(deviceId: Long, newOrder: Io.ListingPlaylistOrder)
    }

    interface ImplApiTranslation {
        suspend fun listing(): Io.ListingTranslation
        suspend fun edit(data: Io.ListingTranslation): Io.ListingTranslation
        suspend fun add(json: String): String
    }

    interface ImplApiRss {
        suspend fun reload(id: Long)
        suspend fun state(id: Long): Io.RssState


        suspend fun listingChannel(): Io.ListingRssChannel
        suspend fun listingInstance(): Io.ListingRssInstance

        suspend fun preview(url: String): api.org.ssoft.server.common.rss.Io.Rss

        suspend fun channelEdit(item: Io.ListingRssChannel.Item)
        suspend fun channelCreate(item: Io.ListingRssChannel.Item): Io.ListingRssChannel.Item

        suspend fun instanceEdit(item: Io.RssInstance.Item)
        suspend fun instanceDelete(rssInstanceId: Long)
        suspend fun instanceCreate(item: Io.RssInstance.Item): Io.RssInstance.Item
        suspend fun instanceDetail(id: Long): Io.RssInstance
        suspend fun instanceTemplate(lang: String): Io.RssInstance
    }

    interface ImplIcons {
        suspend fun random(): IconIO.Result

    }

    interface ImplReport {
        suspend fun listingDeviceSystem(deviceId: Long, offset: Int): IoReport.ListingDeviceSystem
        suspend fun listingDeviceAlive(deviceId: Long, offset: Int): IoReport.ListingDeviceAlive

        suspend fun campaignGroup(companyId: Long, campaignGroupId: Long): Io.ReportCampaign
        suspend fun campaignGroupImpression(companyId: Long, campaignGroupId: Long): IoReport.ReportImpression
        suspend fun appQuiz(companyId: Long, appId: Long, offsetReportId: Long): IoReport.ReportAppQuiz
    }

    interface ImplLicense {
        suspend fun listing(): Io.ServerLicensing
        suspend fun history(companyId: Long): Io.CompanyLicenseHistory
        suspend fun approve(id: Long, companyId: Long, message: String): Io.License
        suspend fun decline(id: Long, companyId: Long, message: String): Io.License
        suspend fun terminate(id: Long, companyId: Long): Io.License
    }

    interface ImplHardware {
        suspend fun listing(): Io.ListingHardware
        suspend fun single(id: Long): Io.Hardware
        suspend fun edit(data: Io.Hardware)
        suspend fun delete(id: Long)
        suspend fun add(data: Io.Hardware): Io.Hardware
    }

    private val defaultThrowFun: (suspend (HttpStatusCode) -> String?) = { statusCode ->
        when (statusCode) {
            HttpStatusCode.BadRequest -> Res.string.err_connection_fail.await()
            HttpStatusCode.NotFound -> Res.string.err_no_data.await()
            HttpStatusCode.Unauthorized -> Res.string.err_unauthorized.await()
            else -> null
        }
    }

    private suspend fun HttpResponse.onFailHandled(function: suspend (HttpStatusCode) -> String? = defaultThrowFun): HttpResponse {
        if (status.isSuccess()) return this
        else error(
            function(status) ?: "${Res.string.err_fail_unexpected.await()} [${status.value}: ${status.description}]"
        )
    }

    fun HttpResponse.throwOnFail(): HttpResponse {
        return if (!status.isSuccess()) error(status.value)
        else this
    }

}

